/**
 * The confirm dialog for something that leaves the building.
 *
 * `ConfirmModal` is the right control for a reversible or internal act: suspend an account,
 * reject a submission, delete a banner. All of those can be undone by the person who did them.
 * A broadcast cannot. Once the mail server has accepted it there is no recall, no edit and no
 * apology that reaches the same inbox.
 *
 * So this one is deliberately harder to satisfy than a button under the cursor:
 *
 *   - the count it will reach is stated as a number, in the dialog, in the largest type on it,
 *   - the operator types a short word to arm the button, and
 *   - the button stays disabled until the typed word matches and stays disabled while the send
 *     is in flight, so a double-click cannot become two sends.
 *
 * The typed word is not security. It is a speed bump placed exactly where muscle memory is,
 * which is the failure this is actually for: nobody sends a broadcast to the wrong list on
 * purpose, they send it by clicking where OK usually is.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from './ui.js';
import { Modal } from './Modal.js';
import { confirmationMatches } from '../lib/emailAudience.js';

export function TypedConfirmModal({
  open,
  title,
  phrase,
  headlineCount,
  headlineText,
  headlineNoun,
  warning,
  confirmLabel,
  loading = false,
  onConfirm,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  /** The word the operator has to type. Short, so nobody reaches for the clipboard. */
  phrase: string;
  /** The number this act reaches. Shown large, because it is the thing to check. */
  headlineCount: number;
  /**
   * The headline as text, when the thing to check is not a count.
   *
   * A balance adjustment's headline is an amount of money, and money is an integer-minor-unit
   * string in this codebase — `headlineCount.toLocaleString()` would mean turning it into a
   * `Number` first, which is the one thing money must never be. When this is set it is rendered
   * instead of the count, already formatted by the caller.
   */
  headlineText?: string;
  /** What the count counts, e.g. "people". */
  headlineNoun: string;
  /** One sentence on why this cannot be undone. */
  warning: string;
  confirmLabel: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}): JSX.Element | null {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reopening must never inherit an armed box from the last time. Clearing on open rather
  // than on close also covers the dialog being closed by Escape mid-type.
  useEffect(() => {
    if (open) {
      setTyped('');
      // A frame later, so the field exists to focus.
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const armed = confirmationMatches(typed, phrase);

  // Escape must not dismiss a dialog whose send is already in flight, and the handler has to be
  // stable: `Modal` binds a key listener keyed on it, and a fresh closure every render rebinds
  // the listener every render.
  const close = useCallback(() => {
    if (!loading) onClose();
  }, [loading, onClose]);

  return (
    <Modal
      open={open}
      title={title}
      onClose={close}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={loading}
            disabled={!armed || loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="send-confirm">
        <div className="send-confirm__count">
          <span className="send-confirm__num">
            {headlineText ?? headlineCount.toLocaleString()}
          </span>
          <span className="send-confirm__noun">{headlineNoun}</span>
        </div>
        <p className="send-confirm__warn" role="alert">
          {warning}
        </p>
        {children}
        <div className="field">
          <label className="field__label" htmlFor="typed-confirm">
            Type <code className="mono">{phrase}</code> to enable the button
          </label>
          {/* A bare input rather than the `Input` primitive: this one needs a ref to take
              focus when the dialog opens, and `Input` does not forward one. Same class, so
              it is the same control to look at. */}
          <input
            className="input"
            id="typed-confirm"
            ref={inputRef}
            value={typed}
            autoComplete="off"
            spellCheck={false}
            disabled={loading}
            placeholder={phrase}
            onChange={(e) => setTyped(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
